import React, { useCallback, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import clsx from 'clsx'
import Image from 'components/Common/Image/Image'
import GeoContext from 'contexts/geoContext'
import { convertAudToRegion } from 'luxPlus/lib/getRegionSaveAmountCopy'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import FormatCurrency from 'components/Common/FormatCurrency'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getThemeClassName } from 'lib/theme/themeUtils'
import Sheet from 'components/utils/Sheet'
import LuxPlusLandingCtaButtons from './LuxPlusLandingCtaButtons'
import useRebrandVersion from 'hooks/useRebrandVersion'

interface TestimonialData {
  id: number
  savedAmountAud: number
  tripsBookedCount: number
  memberPriceAud: number
  year: string
  imageIds: [string, string]
}

const TESTIMONIALS: Array<TestimonialData> = [
  {
    id: 1,
    savedAmountAud: 7860,
    tripsBookedCount: 8,
    memberPriceAud: 63805,
    year: '2025',
    imageIds: [
      'zdj1fokcfc8x3p9n3lmc',
      'pfgi47gx1qni1imqdsc',
    ],
  },
  {
    id: 2,
    savedAmountAud: 4832,
    tripsBookedCount: 2,
    memberPriceAud: 11946,
    year: '2025',
    imageIds: [
      'ucp47vzdbgkc21hktr4l',
      '3jhi8k0g5tfv4ch31kk',
    ],
  },
  {
    id: 3,
    savedAmountAud: 3918,
    tripsBookedCount: 4,
    memberPriceAud: 21346,
    year: '2025',
    imageIds: [
      '3ebhcuzo8udbwr21ifxe',
      'topdxss15464xgajjv',
    ],
  },
  {
    id: 4,
    savedAmountAud: 3260,
    tripsBookedCount: 7,
    memberPriceAud: 70453,
    year: '2025',
    imageIds: [
      'pfgi47gx1qni1imqdsc',
      'zdj1fokcfc8x3p9n3lmc',
    ],
  },
]

interface Props {
  /** Whether testimonial carousel arrow icons should mirror in RTL. */
  arrowIconRtlFlip?: boolean
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const fadeInDim = keyframes`
  from { opacity: 0; }
  to { opacity: 0.6; }
`

const CarouselRoot = styled.div`
  position: relative;
  display: flex;
  overflow: hidden;
  justify-content: center;
  background-color: ${props => props.theme.palette.product.luxPlus.background};
  border-radius: ${props => props.theme.borderRadius.M};
`

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${mediaQueryUp.desktop} {
    flex-direction: row;
  }
`

const ImagesColumn = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  place-items: start;
  min-width: ${rem(320)};
  min-height: ${rem(200)};
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    min-height: ${rem(344)};
    width: ${rem(600)};
    flex-shrink: 0;
    overflow: visible;
  }
`

const BackImageWrapper = styled.div`
  grid-column: 1;
  grid-row: 1;
  width: ${rem(180)};
  height: ${rem(132)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  opacity: 0.6;
  transform: rotate(-5.58deg);
  margin-top: ${rem(24)};
  margin-inline-start: ${rem(12)};
  animation: ${fadeInDim} 2s ease-out forwards;

  ${mediaQueryUp.desktop} {
    width: ${rem(306)};
    height: ${rem(224)};
    margin-top: ${rem(48)};
    margin-inline-start: ${rem(24)};
  }
`

const FrontImageWrapper = styled.div`
  grid-column: 1;
  grid-row: 1;
  width: ${rem(205)};
  height: ${rem(160)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  transform: rotate(2.53deg);
  margin-top: ${rem(36)};
  margin-inline-start: ${rem(110)};
  z-index: 1;
  animation: ${fadeIn} 2s ease-out forwards;
  animation-delay: 0.4s;
  opacity: 0;

  ${mediaQueryUp.desktop} {
    width: ${rem(395)};
    height: ${rem(265)};
    margin-top: ${rem(62)};
    margin-inline-start: ${rem(196)};
  }
`

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding: ${rem(24)} ${rem(20)};
  flex: 1 0 0;
  min-width: 0;

  ${mediaQueryUp.desktop} {
    padding-block: ${rem(40)};
    padding-inline-start: ${rem(59)};
    padding-inline-end: ${rem(40)};
    justify-content: center;
  }
`

const ContentFade = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  animation: ${fadeIn} 1.2s ease-out;
`

const SavedText = styled.span`
  color: ${props => props.theme.palette.highlight.primary.normalForeground};

  &.rebrand {
    color: ${props => props.theme.palette.product.luxPlus.contrast};
  }
`

const PriceComparison = styled.div`
  display: flex;
  gap: ${rem(20)};
  align-items: flex-start;
`

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const PriceDivider = styled.div`
  width: ${rem(1)};
  align-self: stretch;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
`

const MemberPriceLabel = styled(Caption)`
  color: ${props => props.theme.palette.product.luxPlus.foreground};
  font-weight: 600;
`

const MemberPriceValue = styled.span`
  color: ${props => props.theme.palette.product.luxPlus.foreground};
  font-size: ${rem(24)};
  font-weight: 500;
  line-height: ${rem(28)};
`

const CarouselArrow = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  inset-inline-end: ${rem(12)};
  z-index: 2;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

function LuxPlusTestimonialCarousel({ arrowIconRtlFlip = true }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const isRebrand = useRebrandVersion() === 'rebrand'
  const darkCtaClassName = isRebrand ? getThemeClassName({ mobile: 'default' }) : undefined
  const darkCtaVariant = isRebrand ? 'dark' : undefined
  const carouselArrowKind = isRebrand ? 'primary' : 'secondary'
  const savedTextClassName = clsx({ rebrand: isRebrand })

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length)
  }, [])

  const testimonial = TESTIMONIALS[currentIndex]
  const savedAmount = convertAudToRegion(testimonial.savedAmountAud, currentRegionCode)
  const memberPrice = convertAudToRegion(testimonial.memberPriceAud, currentRegionCode)
  const nonMemberPrice = memberPrice + savedAmount

  const savedLabel = intl.formatMessage({
    id: 'a1PT1q6HmrF8eOlghtTQ',
    defaultMessage: 'saved',
    description: 'Verb in testimonial heading indicating how much a member saved',
  })

  const withProgramInYearLabel = intl.formatMessage(
    {
      id: 'qk6tZRUMFPY6sh_iEMHR',
      defaultMessage: 'with {programName} in {year}',
      description: 'Continuation of testimonial heading indicating the program and year of savings',
    },
    { programName: LUXURY_PLUS.PROGRAM_NAME, year: testimonial.year },
  )

  const memberPaidLabel = intl.formatMessage({
    id: 'LXzLjb5xd-w4MQ_8e9X6',
    defaultMessage: 'The member paid',
    description: 'Label showing what price the LuxPlus member paid for their booking',
  })

  const anonymousMemberName = intl.formatMessage({
    id: 'lcpkd64zK064C7qkVwEM',
    defaultMessage: 'A member',
    description: 'Name of the anonymous member in the testimonial carousel',
  })
  const tripsBookedLabel = intl.formatMessage(
    {
      id: 'CbEbCqJWMwMe8XczLB3m',
      defaultMessage: '{count, plural, one {# trip booked} other {# trips booked}}',
      description: 'Trip count label shown in the LuxPlus testimonial carousel for the anonymous member',
    },
    { count: testimonial.tripsBookedCount },
  )

  const nonMembersPaidLabel = intl.formatMessage({
    id: '1_9PJrrwoZNi6rN7S4Bb',
    defaultMessage: 'Non-members paid',
    description: 'Label showing what price non-members paid for the same booking',
  })

  const becomeMemberLabel = intl.formatMessage({
    id: 'isEHZL5ENeDHW4VPIQSm',
    defaultMessage: 'Become a member',
    description: 'CTA button text to become a LuxPlus member on testimonial carousel',
  })

  const startFreePreviewLabel = intl.formatMessage({
    id: 'NKY0lv7wKaY8firPSm6q',
    defaultMessage: 'Start 28-day free preview',
    description: 'CTA button text to start a free LuxPlus preview on testimonial carousel',
  })

  const priceDisclaimerLabel = intl.formatMessage({
    id: 'o6o5xM5GMNIr2EU8M7Gj',
    defaultMessage: '*Price correct at time of booking',
    description: 'Disclaimer text about price accuracy in testimonial carousel',
  })

  const nextTestimonialLabel = intl.formatMessage({
    id: '-y3pxMVXS2xURnKpYWtV',
    defaultMessage: 'Next testimonial',
    description: 'Aria label for button to navigate to next testimonial in carousel',
  })

  return (
    <Sheet background="lux-plus" padding="24 0" tabletPadding="40 0">
      <LayoutContainer>
        <CarouselRoot className={getThemeClassName({ mobile: 'inverse' })}>
          <CardWrapper key={testimonial.id}>
            <ImagesColumn>
              <BackImageWrapper key={`back-${testimonial.id}-${currentIndex}`}>
                <Image id={testimonial.imageIds[0]} fit="center" dpr={2} alt="" />
              </BackImageWrapper>
              <FrontImageWrapper key={`front-${testimonial.id}-${currentIndex}`}>
                <Image id={testimonial.imageIds[1]} fit="center" dpr={2} alt="" />
              </FrontImageWrapper>
            </ImagesColumn>
            <ContentColumn>
              <ContentFade key={`content-${testimonial.id}-${currentIndex}`}>
                <Heading variant="heading2" colour="normal-contrast">
                  <SavedText className={savedTextClassName}>{anonymousMemberName} </SavedText>
                  <SavedText className={savedTextClassName}><em>{savedLabel}</em> <FormatCurrency value={savedAmount} format="rounded" /></SavedText>
                  <br />
                  {withProgramInYearLabel}
                </Heading>

                <BodyText variant="medium" colour="normal-contrast">
                  {tripsBookedLabel}
                </BodyText>

                <PriceComparison>
                  <PriceBlock>
                    <MemberPriceLabel variant="medium">{memberPaidLabel}</MemberPriceLabel>
                    <MemberPriceValue><FormatCurrency value={memberPrice} format="rounded" /></MemberPriceValue>
                  </PriceBlock>
                  <PriceDivider />
                  <PriceBlock>
                    <Caption variant="medium" colour="normal-contrast" weight="bold">{nonMembersPaidLabel}</Caption>
                    <Heading variant="heading4" colour="normal-contrast">
                      <FormatCurrency value={nonMemberPrice} format="rounded" />
                    </Heading>
                  </PriceBlock>
                </PriceComparison>

                <div className={darkCtaClassName}>
                  <LuxPlusLandingCtaButtons
                    becomeMemberLabel={becomeMemberLabel}
                    startFreePreviewLabel={startFreePreviewLabel}
                    gap={16}
                    switchRegionLogoColour="inherit"
                    wrap="wrap"
                    variant={darkCtaVariant}
                  />
                </div>

                <BodyText variant="small" colour="light-foreground-alt">
                  {priceDisclaimerLabel}
                </BodyText>
              </ContentFade>
            </ContentColumn>
          </CardWrapper>
          <CarouselArrow
            className={getThemeClassName({ mobile: 'default' })}
            kind={carouselArrowKind}
            variant="dark"
            shape="circle"
            onClick={handleNext}
            aria-label={nextTestimonialLabel}
          >
            <LineAngleRightIcon rtlFlip={arrowIconRtlFlip} />
          </CarouselArrow>
        </CarouselRoot>
      </LayoutContainer>
    </Sheet>
  )
}

export default LuxPlusTestimonialCarousel
